import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const Label = styled.label`
  font-size: 0.75rem;
  color: ${({ theme, color }) => theme.colors[color]};
`;
const InputsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 180px;

  input[type='number'] {
    -moz-appearance: textfield;
  }
  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;
const InputTag = styled.input`
  width: 39px;
  height: 43px;
  background: transparent;
  color: ${({ theme, color }) => theme.colors[color]};
  border: none;
  border-bottom: 2px solid ${({ theme, color }) => theme.colors[color]};
  font-weight: bold;
  font-size: 26px;
  text-align: center;
  outline: none;
`;

export default function Digits({
  type,
  onChange,
  values,
  label,
  forceFocus,
  color,
  ...rest
}) {
  const [focusedInput, setFocusedInput] = useState('digit_1');
  const inputRef = useRef([]);
  const onlyNumbers = new RegExp(/^[0-9]+$/);

  useEffect(() => {
    if (forceFocus) {
      setFocusedInput(forceFocus);
    }
  }, [forceFocus]);

  function isValueValid(value) {
    return value !== '' && onlyNumbers.test(value);
  }

  const autoTab = event => {
    const { nextSibling } = event?.target;
    if (nextSibling) {
      setFocusedInput(nextSibling?.name);
    }
  };

  function handleOnChange(event) {
    const { value, name } = event.target;

    if (isValueValid(value)) {
      const lastTypedValue = value.substring(value.length - 1);

      onChange({ key: name, value: lastTypedValue });
      autoTab(event);
    }
  }

  function handleOnKeyUp(event) {
    const { name, value } = event.target;
    const BACKSPACE_KEY = 8;
    const DELETE_KEY = 46;

    if (event.keyCode === BACKSPACE_KEY || event.keyCode === DELETE_KEY) {
      if (value === '') {
        const { previousSibling } = event?.target;
        if (previousSibling) {
          onChange({ key: previousSibling?.name, value: '' });
          setFocusedInput(previousSibling?.name);
        }
      } else {
        onChange({ key: name, value: '' });
        setFocusedInput(name);
      }
    }
  }

  const Input = props => {
    const { index } = props;
    const name = `digit_${index}`;
    return (
      <InputTag
        role="textbox"
        aria-label={`dígito ${index}`}
        autoFocus={name === focusedInput}
        ref={input => {
          inputRef.current[index] = input;
        }}
        data-index={index}
        onChange={handleOnChange}
        onKeyUp={handleOnKeyUp}
        name={name}
        type={type}
        value={values?.[name] || ''}
        autoComplete="new-password" // Trying to avoid autocomplete
        color={color}
      />
    );
  };

  Input.propTypes = {
    index: PropTypes.number,
  };

  return (
    <Container {...rest}>
      <Label color={color}>{label}</Label>
      <InputsContainer>
        {Array.from({ length: 4 }, (item, index) => (
          <Input key={index} index={index + 1} />
        ))}
      </InputsContainer>
    </Container>
  );
}

Digits.propTypes = {
  type: PropTypes.string,
  onChange: PropTypes.func,
  values: PropTypes.object,
  label: PropTypes.string,
  forceFocus: PropTypes.string,
};
